import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import type { SkillTag } from "../../client";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "../../components/drawer";
import SkillPicker from "./SkillPicker";

/**
 * SkillPickerDrawer — presents {@link SkillPicker} as a right-side drawer on
 * desktop and a bottom sheet on mobile (responsive `direction`).
 */

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addedSkills: SkillTag[];
  onAddSkill: (tag: SkillTag) => void;
  onRemoveSkill: (name: string) => void;
  suggestedSkills?: SkillTag[];
  suggestedLabel?: string;
}

export default function SkillPickerDrawer({
  open,
  onOpenChange,
  addedSkills,
  onAddSkill,
  onRemoveSkill,
  suggestedSkills,
  suggestedLabel,
}: Props) {
  const isMobile = useIsMobile();
  const direction = isMobile ? "bottom" : "right";

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction={direction}
      shouldScaleBackground={false}
    >
      <DrawerContent
        direction={direction}
        onClose={() => onOpenChange(false)}
        className={
          direction === "right"
            ? "h-full w-[420px] max-w-[92vw]"
            : "max-h-[85vh]"
        }
      >
        <div className="flex items-center justify-between border-b border-gray-3 px-5 py-4">
          <div className="flex flex-col">
            <DrawerTitle className="text-base font-semibold">
              Skills
            </DrawerTitle>
            <DrawerDescription className="text-xs text-gray-5">
              Search the catalogue or create a custom skill.
            </DrawerDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full p-2 text-gray-6 hover:bg-gray-1"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <SkillPicker
            addedSkills={addedSkills}
            onAddSkill={onAddSkill}
            onRemoveSkill={onRemoveSkill}
            suggestedSkills={suggestedSkills}
            suggestedLabel={suggestedLabel}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
